import Swal from "sweetalert2";
import { baseURL } from "./api";
import { toHundredths, formatTwoDecimals } from "./money";

export const globalStyle = {
  bgColor: "#F4F5F6",
  bgColorWhite: "#f0f2f7",
  bgColorBlue: "#1a284f",
  buttonColor: "linear-gradient(#3A80FB, #6A3DC1)",
  stepColor: "linear-gradient(#B4CCF7, #B4CCF7)",
  blueColor: "#3A80FB",
  curveDarkColor: "#1a284f",
  curveLightColor: "#EBF2F4",
  fontSize: 16,
  headingFontSize: 32,
  headingFontSizeSmall: 18,
  fontFamilyHeading: "Inter",
  fontFamily: "'Roboto Serif', serif",
  fontFamilyParagraph: "Rubik",
  fontFamilyButton: "'Roboto Serif', serif",
  borderRadius: 4,
  marginBetween: 1.5,
};

export const handleDangerVisible = (err) => {
  Swal.fire({
    title: "Error",
    text: err,
    icon: "error",
    timer: 30000,
    didOpen: () => {
      const container = document.querySelector(".swal2-container");
      if (container) {
        container.style.zIndex = "2000";
      }
    },
  });
};

export const handleVisible = (msg) => {
  Swal.fire({
    title: "Success",
    text: msg,
    icon: "success",
    timer: 30000,
    didOpen: () => {
      const container = document.querySelector(".swal2-container");
      if (container) {
        container.style.zIndex = "2000";
      }
    },
  });
};

export const handleVisibleForEdit = (msg, successURL) => {
  Swal.fire({
    icon: "success",
    title: "Success",
    text: msg,
    confirmButtonText: "Okay",
    didOpen: () => {
      const container = document.querySelector(".swal2-container");
      if (container) {
        container.style.zIndex = "2000";
      }
    },
  }).then((result) => {
    if (result.isConfirmed) {
      successURL();
    }
  });
};

export const validateEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const formatId = (value, type) => {
  if (!value) return "";
  if (type === "NTN") {
    return String(value).replace(/[^a-zA-Z0-9]/g, "").slice(0, 7);
  }
  const v = String(value).replace(/\D/g, "");
  if (type === "CNIC") {
    return v
      .slice(0, 13)
      .replace(/(\d{5})(\d{7})(\d{1})?/, (_, p1, p2, p3) =>
        p3 ? `${p1}-${p2}-${p3}` : `${p1}-${p2}`,
      );
  }
  return v;
};

// Company-level Section 236H rates (%). Which one an invoice gets follows the
// buyer's standing on the Active Taxpayer List: "Register" is the rate for a
// buyer who is Active on the ATL, "NonRegister" the rate for everyone else.
// (The names date from when they followed the buyer's registration type.)
export const SECTION_236H_FIELDS = [
  "GSection236HRegister",
  "GSection236HNonRegister",
];

// The invoice's GSection 236H % for this company and a buyer with this ATL
// status: the company's Active rate when the buyer is Active on the ATL, its
// Non-Active rate for anyone else - Inactive, not checked, or with no NTN /
// CNIC to be on the list by. Never a fixed number: always the company's own.
// Mirrors the backend's services/section236H.js.
export const section236HRateFor = (company, atlStatus) =>
  Number(
    atlStatus === "Active"
      ? company?.GSection236HRegister
      : company?.GSection236HNonRegister,
  ) || 0;

// Section 236H is shown after an invoice's grand total but never added into the
// invoice's own totals (it is not part of the value reported to FBR):
//
//   Grand Total              100,000.00
//   GSection 236H @ 0.5%         500.00   <- rate x grand total
//   Final Amount             100,500.00   <- grand total + 236H
//
// Worked in whole paisa - the total in paisa times the rate in hundredths of a
// percent - so there is no floating-point drift and a half paisa rounds up
// (toHundredths: global/money.js).

const section236HPaisa = (grandTotal, rate) =>
  Math.round((toHundredths(grandTotal) * toHundredths(rate)) / 10000);

export const section236HAmountFor = (grandTotal, rate) =>
  section236HPaisa(grandTotal, rate) / 100;

export const section236HFinalAmountFor = (grandTotal, rate) =>
  (toHundredths(grandTotal) + section236HPaisa(grandTotal, rate)) / 100;

export const section236HLabel = (rate) =>
  `GSection 236H @ ${toHundredths(rate) / 100}%`;

export const SECTION_236H_FINAL_LABEL = "Final Amount";

// The 236H line (and the Final Amount under it, which would only repeat the
// grand total) is shown only when the invoice actually carries a 236H rate.
export const hasSection236H = (rate) => toHundredths(rate) > 0;

// Section 236G is worked exactly like 236H - the invoice's own rate times its
// grand total, in whole paisa - and, like 236H, is never part of the invoice's
// own totals.
export const section236GAmountFor = (grandTotal, rate) =>
  section236HPaisa(grandTotal, rate) / 100;

// Digits with at most two decimal places. Keeps a partially typed value
// ("12", "12.", "12.5") usable while the user is still typing.
export const sanitizeDecimalInput = (value) =>
  String(value ?? "")
    .replace(/[^0-9.]/g, "")
    .match(/^\d*(\.\d{0,2})?/)?.[0] ?? "";

// A money value on screen: rounded (a third decimal of 5 or more rounds up)
// and always written with exactly two decimals - "1095.26", "1095.20". Blank
// for no value. It used to cut the extra decimals off instead of rounding.
export const displayTwoDecimalDigits = (num) => {
  if (num === null || num === undefined || num === "" || isNaN(num)) return "";
  return formatTwoDecimals(num);
};

// The API returns a logo either as a bare file name or as an absolute URL built
// from the backend's DOMAIN env var, which does not necessarily point at the API
// this build actually talks to. Resolve it against baseURL so the image loads in
// every environment.
export const getLogoUrl = (logo) => {
  if (!logo) return "";
  const value = String(logo).trim();
  if (!value) return "";
  if (value.startsWith("data:") || value.startsWith("blob:")) return value;
  // Anything hosted elsewhere is left untouched; only our own /assets/ URLs and
  // bare file names get rebuilt.
  const isOwnAsset = !value.includes("://") || value.includes("/assets/");
  if (!isOwnAsset) return value;
  const fileName = value.split("/").pop().split("?")[0];
  return fileName ? `${baseURL}/assets/${fileName}` : "";
};

/**
 * Page size that means "the whole list, please".
 *
 * The list endpoints page at 25 rows unless the request names a limit, so the
 * screens that feed a dropdown - which pass "" for page and limit because they
 * want every row - were silently getting only the first 25 users, companies,
 * buyers or items. Dropdowns must offer the complete list, so an unset limit is
 * sent as this explicit one instead.
 */
export const ALL_RECORDS_LIMIT = 100000;

const isUnset = (value) => value === "" || value === null || value === undefined;

/** Normalises a screen's (page, limit) pair into query-string values. */
export const listPaging = (page, limit) => ({
  page: isUnset(page) ? 0 : page,
  limit: isUnset(limit) ? ALL_RECORDS_LIMIT : limit,
});
